import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ALL_ISO_CODES } from '../../../data/isoCodes';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import JournalForm from '../../ScientificManagement/Journal/JournalForm';
import { useCountries } from '../../../hooks/useCountries';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, itemTemplate }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label,searchKey"
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            itemTemplate={itemTemplate}
            emptyFilterMessage="Không tìm thấy kết quả"
        />
    );
};

const ScientificArticlesMngtForm = ({
    isOpen, onClose, onSubmit, formData, setFormData, isEditing,
    fields = [], articleTypes = [], users = [],
    members = [], setMembers,
    journalTypes = [],
    isAdminMode = false, toast,
    readOnly = false, fetchData
}) => {
    const { countries } = useCountries();
    const isFirstLoad = useRef(true);

    const [isJournalModalOpen, setIsJournalModalOpen] = useState(false);
    const [journals, setJournals] = useState([]);
    const [searchJournal, setSearchJournal] = useState('');
    const [languageOptions, setLanguageOptions] = useState([]);

    const [doiInput, setDoiInput] = useState('');
    const [isExtracting, setIsExtracting] = useState(false);
    const [previewScores, setPreviewScores] = useState(null);

    const initialJournalForm = {
        Name: '', JournalTypeId: '', ISSN: '', Score: '',
        Country: 'Việt Nam', Publisher: '', ImpactFactor: '', QCategory: '', IsHDCGS: false
    };
    const [isCreateJournalModalOpen, setIsCreateJournalModalOpen] = useState(false);
    const [journalFormData, setJournalFormData] = useState(initialJournalForm);

    const [pendingFiles, setPendingFiles] = useState([]);
    const [deletedFileIds, setDeletedFileIds] = useState([]);
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);

    const journalCategories = ["SCIE", "SSCI", "AHCI", "SCOPUS", "ESCI", "Khác"];

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const userOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || u.email || '';
            const dept = u.DepartmentName || '';
            const displayLabel = email ? `${fullName} - ${email}` : fullName;
            const searchStr = superCleanString(`${fullName} ${email} ${dept}`);

            return { label: displayLabel, value: u.Id, searchKey: searchStr, fullName: fullName, email: email, dept: dept };
        });
    }, [users]);

    const userItemTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>{option.email} {option.dept ? ` - ${option.dept}` : ''}</span>
            </div>
        );
    };

    const fetchJournals = useCallback(() => {
        fetch(`${process.env.REACT_APP_API_BASE_URL}/journal`, {
            headers: getAuthHeaders()
        })
            .then(res => res.json())
            .then(data => setJournals(data))
            .catch(err => console.error("Lỗi tải tạp chí:", err));
    }, [getAuthHeaders]);

    useEffect(() => {
        if (isOpen) {
            isFirstLoad.current = true;
            setDoiInput('');
            setPreviewScores(null);
            setPendingFiles([]);
            setDeletedFileIds([]);

            const viTranslator = new Intl.DisplayNames(['vi'], { type: 'language' });

            const allLangs = ALL_ISO_CODES.map(code => {
                let nameVi = viTranslator.of(code) || code;
                nameVi = nameVi.charAt(0).toUpperCase() + nameVi.slice(1);
                return { code, name: nameVi };
            }).filter(lang => lang.name.toLowerCase() !== lang.code.toLowerCase());

            const priorityCodes = ['vi', 'en', 'fr', 'ru'];
            const priorityLangs = priorityCodes
                .map(code => allLangs.find(l => l.code === code))
                .filter(Boolean);

            const otherLangs = allLangs
                .filter(l => !priorityCodes.includes(l.code))
                .sort((a, b) => a.name.localeCompare(b.name));

            setLanguageOptions([...priorityLangs, ...otherLangs]);

            setFormData(prev => {
                if (!prev.LanguageName) {
                    return { ...prev, LanguageName: 'Tiếng Việt' };
                }
                return prev;
            });

            fetchJournals();
        }
    }, [isOpen, fetchJournals, setFormData]);

    useEffect(() => {
        if (isOpen) {
            setMembers(prevMembers => {
                if (!prevMembers || prevMembers.length === 0) return prevMembers;
                return prevMembers;
            });
        }
    }, [isOpen, setMembers]);

    useEffect(() => {
        const currentUserStr = localStorage.getItem('user');
        const currentUserInfo = currentUserStr ? JSON.parse(currentUserStr) : null;
        const isCreator = !isEditing || (formData.UserId?.toString() === currentUserInfo?.Id?.toString());
        const isReadOnlyFormMode = isEditing && !(isCreator || isAdminMode);
        const finalDisabled = readOnly || isReadOnlyFormMode;

        if (finalDisabled || !members) return;

        if (isEditing && isFirstLoad.current) {
            isFirstLoad.current = false;
            return;
        }

        const total = members.length > 0 ? members.length : 1;

        const primaryCount = members.filter(m =>
            m.IsFirstAuthor || m.isFirstAuthor || m.IsContactAuthor || m.isContactAuthor
        ).length;
        const primary = primaryCount > 0 ? primaryCount : 1;

        const contactCount = members.filter(m =>
            m.IsContactAuthor || m.isContactAuthor
        ).length;
        const contact = contactCount > 0 ? contactCount : 1;

        setFormData(prev => {
            if (prev.TotalAuthors !== total || prev.PrimaryAuthors !== primary || prev.ContactAuthors !== contact) {
                return {
                    ...prev,
                    TotalAuthors: total,
                    PrimaryAuthors: primary,
                    ContactAuthors: contact
                };
            }
            return prev;
        });
    }, [members, readOnly, isEditing, isAdminMode, formData.UserId, setFormData]);

    const expectedTotal = members.length > 0 ? members.length : 1;
    const expectedPrimaryCount = members.filter(m => m.IsFirstAuthor || m.isFirstAuthor || m.IsContactAuthor || m.isContactAuthor).length;
    const expectedPrimary = expectedPrimaryCount > 0 ? expectedPrimaryCount : 1;
    const expectedContactCount = members.filter(m => m.IsContactAuthor || m.isContactAuthor).length;
    const expectedContact = expectedContactCount > 0 ? expectedContactCount : 1;

    const isOverridden = (Number(formData.TotalAuthors) !== expectedTotal) ||
        (Number(formData.PrimaryAuthors) !== expectedPrimary) ||
        (Number(formData.ContactAuthors) !== expectedContact);


    const existingFiles = useMemo(() => {
        if (!formData.EvidenceFileName) return [];
        try {
            const parsed = JSON.parse(formData.EvidenceFileName);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }, [formData.EvidenceFileName]);

    const handleCreateJournalSubmit = async (e) => {
        e.preventDefault();

        const nameText = journalFormData.Name;
        if (nameText && nameText.trim().length >= 5) {
            const normalizeString = (str) => {
                if (!str) return '';
                return str.toLowerCase().trim()
                    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                    .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                    .replace(/[^a-z0-9]/g, "");
            };

            const calculateSimilarity = (s, t) => {
                if (!s || !t) return 0;
                if (s === t) return 1;
                const d = Array(s.length + 1).fill(null).map(() => Array(t.length + 1).fill(null));
                for (let i = 0; i <= s.length; i++) d[i][0] = i;
                for (let j = 0; j <= t.length; j++) d[0][j] = j;
                for (let j = 1; j <= t.length; j++) {
                    for (let i = 1; i <= s.length; i++) {
                        const cost = s[i - 1] === t[j - 1] ? 0 : 1;
                        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
                    }
                }
                return 1.0 - (d[s.length][t.length] / Math.max(s.length, t.length));
            };

            const normInput = normalizeString(nameText);
            const inputIssn = journalFormData.ISSN ? journalFormData.ISSN.replace(/-/g, '').trim() : '';

            let isDuplicate = false;

            for (let item of journals) {
                const normExisting = normalizeString(item.Name);
                const sim = calculateSimilarity(normInput, normExisting);
                const existingIssn = item.ISSN ? item.ISSN.replace(/-/g, '').trim() : '';

                if (sim > 0.90) {
                    if (sim > 0.98 || (inputIssn && inputIssn === existingIssn)) {
                        isDuplicate = true;
                        toast?.current?.show({
                            severity: 'error',
                            summary: 'Lỗi trùng lặp dữ liệu',
                            detail: `Tạp chí này đã tồn tại trên hệ thống: "${item.Name}". Vui lòng kiểm tra lại!`,
                            life: 5000
                        });
                        break;
                    }
                }
            }

            if (isDuplicate) return;
        }

        const currentUserStr = localStorage.getItem('user');
        const currentUser = currentUserStr ? JSON.parse(currentUserStr) : {};

        const payload = {
            ...journalFormData,
            JournalTypeId: parseInt(journalFormData.JournalTypeId),
            Score: parseFloat(journalFormData.Score),
            CreatedBy: currentUser.Id
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/journal`, {
                method: 'POST',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            let result;
            try {
                result = await response.json();
            } catch (e) {
                result = { status: 'error', message: 'Lưu thất bại!' };
            }

            if (response.ok && result.status !== 'error' && result.status !== 'fail') {
                toast?.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: 'Đã thêm mới tạp chí!',
                    life: 3000
                });

                fetchJournals();
                setSearchJournal(journalFormData.Name);

                setIsCreateJournalModalOpen(false);
                setJournalFormData(initialJournalForm);
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Không thể lưu',
                    detail: result.message || 'Tạp chí đã tồn tại hoặc dữ liệu không hợp lệ!',
                    life: 5000
                });
            }
        } catch (error) {
            toast?.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: 'Lỗi kết nối máy chủ!',
                life: 3000
            });
        }
    };

    const handleTitleBlur = async (e) => {
        if (readOnly) return;
        const titleText = e.target.value;
        if (!titleText || titleText.trim().length < 5) return;

        try {
            const userStr = localStorage.getItem('user');
            const currentUser = userStr ? JSON.parse(userStr) : null;
            const userId = currentUser ? currentUser.Id : '';

            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isAdmin = userRoleName === 'admin' || currentUser?.RoleId === 1 ||
                (userRoleName.includes('trưởng khoa') && currentUser?.IsDelegatedAdmin === true);

            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const response = await fetch(`${baseUrl}/scientificarticlemngt?userId=${userId}&isAdmin=${isAdmin}`, {
                headers: {
                    'X-User-Role-Id': currentUser?.RoleId?.toString() || '',
                    'X-User-Dept-Id': currentUser?.DepartmentId?.toString() || ''
                }
            });

            if (response.ok) {
                const existingData = await response.json();

                const normalizeString = (str) => {
                    if (!str) return '';
                    return str.toLowerCase().trim()
                        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                        .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                        .replace(/[^a-z0-9]/g, "");
                };

                const calculateSimilarity = (s, t) => {
                    if (!s || !t) return 0;
                    if (s === t) return 1;
                    const d = Array(s.length + 1).fill(null).map(() => Array(t.length + 1).fill(null));
                    for (let i = 0; i <= s.length; i++) d[i][0] = i;
                    for (let j = 0; j <= t.length; j++) d[0][j] = j;
                    for (let j = 1; j <= t.length; j++) {
                        for (let i = 1; i <= s.length; i++) {
                            const cost = s[i - 1] === t[j - 1] ? 0 : 1;
                            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
                        }
                    }
                    return 1.0 - (d[s.length][t.length] / Math.max(s.length, t.length));
                };

                const normInput = normalizeString(titleText);
                const currentId = isEditing ? formData.Id : 0;
                const inputYear = formData.PublicationDate ? formData.PublicationDate.substring(0, 4) : '';

                for (let item of existingData) {
                    if (item.Id === currentId) continue;

                    const normExisting = normalizeString(item.Title);
                    const sim = calculateSimilarity(normInput, normExisting);
                    const existingYear = item.PublicationDate ? item.PublicationDate.substring(0, 4) : '';

                    if (sim > 0.90) {
                        if (sim > 0.98 || existingYear === inputYear || (formData.JournalId && item.JournalId === formData.JournalId)) {
                            toast?.current?.show({
                                severity: 'warn',
                                summary: 'Cảnh báo trùng lặp',
                                detail: `Hệ thống phát hiện bài báo rất giống đã tồn tại: "${item.Title}". Vui lòng kiểm tra lại!`,
                                life: 8000
                            });
                            break;
                        }
                    }
                }
            }
        } catch (err) {
            console.error("Lỗi check trùng lặp:", err);
        }
    };

    const handleDoiExtraction = async () => {
        if (readOnly) return;
        if (!doiInput || !doiInput.trim()) {
            toast?.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập đường link hoặc mã DOI!', life: 3000 });
            return;
        }

        setIsExtracting(true);
        try {
            const userStr = localStorage.getItem('user');
            const currentUser = userStr ? JSON.parse(userStr) : null;
            const userId = currentUser ? currentUser.Id : '';

            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const response = await fetch(`${baseUrl}/extract-doi?doi=${encodeURIComponent(doiInput.trim())}&userId=${userId}`);

            if (!response.ok) throw new Error('Không thể lấy dữ liệu từ DOI này.');

            const result = await response.json();
            if (result.status === 'error') throw new Error(result.message);

            const metadata = result.data;
            let updateData = { ...formData };

            if (metadata.title) updateData.Title = metadata.title;
            if (metadata.publisher) updateData.Publisher = metadata.publisher;

            if (metadata.publication_date) {
                try {
                    const dateObj = new Date(metadata.publication_date);
                    if (!isNaN(dateObj.getTime())) updateData.PublicationDate = dateObj.toISOString().split('T')[0];
                } catch (e) { console.error("Lỗi convert ngày:", e); }
            } else if (metadata.year) {
                updateData.PublicationDate = `${metadata.year}-01-01`;
            }

            if (metadata.scholar_link) updateData.ArticleUrl = metadata.scholar_link;
            else if (metadata.publisher_link) updateData.ArticleUrl = metadata.publisher_link;
            else if (metadata.doi) updateData.ArticleUrl = metadata.doi.startsWith('http') ? metadata.doi : `https://doi.org/${metadata.doi}`;

            if (metadata.authors && Array.isArray(metadata.authors)) {
                const authorNames = metadata.authors.map(a => a.name || a.display_name).filter(Boolean);
                if (authorNames.length > 0) {
                    updateData.OriginalAuthors = authorNames.join(', ');
                    updateData.TotalAuthors = authorNames.length;

                    let primaryCount = 0;
                    let contactCount = 0;

                    const normalizeVietnamese = (str) => {
                        if (!str) return '';
                        return str.toLowerCase()
                            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                            .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                            .replace(/[^a-z0-9\s]/g, "")
                            .trim();
                    };

                    const matchUserByName = (authorName, usersList) => {
                        if (!authorName) return null;
                        const normAuthor = normalizeVietnamese(authorName);
                        const authorParts = normAuthor.split(/\s+/).filter(Boolean);
                        const sortedAuthor = [...authorParts].sort().join(' ');

                        for (let u of usersList) {
                            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
                            const normU = normalizeVietnamese(fullName);
                            const uParts = normU.split(/\s+/).filter(Boolean);
                            const sortedU = [...uParts].sort().join(' ');

                            if (sortedAuthor === sortedU) return u;

                            if (normU === normAuthor) return u;

                            const firstNameU = normalizeVietnamese(u.FirstName);
                            const lastNameU = normalizeVietnamese(u.LastName);

                            if (firstNameU && authorParts.includes(firstNameU)) {
                                const remainingAuthor = authorParts.filter(p => p !== firstNameU);
                                if (remainingAuthor.length === 0 || remainingAuthor.some(p => lastNameU.includes(p))) {
                                    return u;
                                }
                            }
                        }
                        return null;
                    };

                    const matchedMembers = [];

                    metadata.authors.forEach((authorData) => {
                        if (!authorData.name) return;
                        const authorName = authorData.name;

                        const matchedUser = matchUserByName(authorName, users);

                        const isFirst = authorData.author_position === 'first';
                        const isContact = authorData.is_corresponding === true;
                        const orgCount = parseInt(authorData.org_count) || 1;

                        if (isFirst || isContact) primaryCount++;
                        if (isContact) contactCount++;

                        if (matchedUser) {
                            if (!matchedMembers.some(m => m.UserId === matchedUser.Id)) {
                                matchedMembers.push({
                                    UserId: matchedUser.Id,
                                    FullName: `${matchedUser.LastName || ''} ${matchedUser.FirstName || ''}`.trim(),
                                    IsFirstAuthor: isFirst,
                                    IsContactAuthor: isContact,
                                    IsUD: true,
                                    OrganizationCount: orgCount,
                                    IsStudent: false,
                                    IsInstructor: true
                                });
                            }
                        } else {
                            matchedMembers.push({
                                UserId: '',
                                FullName: authorName,
                                IsFirstAuthor: isFirst,
                                IsContactAuthor: isContact,
                                IsUD: false,
                                OrganizationCount: orgCount,
                                IsStudent: true,
                                IsInstructor: true
                            });
                        }
                    });

                    if (matchedMembers.length > 0) {
                        setMembers(matchedMembers);
                    }

                    updateData.PrimaryAuthors = primaryCount > 0 ? primaryCount : 1;
                    updateData.ContactAuthors = contactCount > 0 ? contactCount : 1;
                }
            }

            let detectedQRanking = null;
            try {
                const cachedSci = sessionStorage.getItem('scimagoRankingDict');
                if (cachedSci && metadata.journal_name) {
                    const rankingDict = JSON.parse(cachedSci);
                    let rawName = metadata.journal_name.toLowerCase().replace(/[^a-z0-9]/g, '');
                    if (rankingDict[rawName]) {
                        detectedQRanking = rankingDict[rawName];
                    } else if (rawName.length > 10) {
                        const sortedKeys = Object.keys(rankingDict).sort((a, b) => b.length - a.length);
                        for (let key of sortedKeys) {
                            if (key.length > 10 && rawName.includes(key)) {
                                detectedQRanking = rankingDict[key];
                                break;
                            }
                        }
                    }
                }
            } catch (e) { }

            if (metadata.internal_journal_id) {
                updateData.JournalId = metadata.internal_journal_id;
                updateData.JournalName = metadata.internal_journal_name || metadata.journal_name;
                updateData.ISSN_ISBN = metadata.issn || '';
                updateData.JournalType = metadata.internal_journal_type || '';
                updateData.JournalScore = metadata.internal_journal_score || 0;
                updateData.JournalStatus = metadata.internal_journal_status || '';

                updateData.QRanking = detectedQRanking || metadata.internal_q_category || '';
                updateData.BonusCoefficient = '1.0';

                toast?.current?.show({ severity: 'success', summary: 'Ánh xạ Tạp chí thành công', detail: `Hệ thống nhận diện: ${updateData.JournalName} (Điểm: ${updateData.JournalScore})`, life: 5000 });
            } else if (metadata.journal_name) {
                updateData.JournalName = metadata.journal_name;
                updateData.ISSN_ISBN = metadata.issn || '';
                updateData.JournalId = null;
                updateData.JournalType = detectedQRanking ? 'Tạp chí Quốc tế' : '';
                updateData.JournalScore = 0;
                updateData.JournalStatus = '';

                if (detectedQRanking) {
                    updateData.QRanking = detectedQRanking;
                    updateData.BonusCoefficient = '1.0';
                    toast?.current?.show({ severity: 'info', summary: 'Phát hiện tạp chí SCImago', detail: `Hệ thống nhận diện đây là tạp chí ${detectedQRanking}!`, life: 6000 });
                } else {
                    toast?.current?.show({ severity: 'warn', summary: 'Lưu ý', detail: 'Vui lòng chọn tạp chí từ danh mục!', life: 6000 });
                }
            }

            if (metadata.issn) {
                updateData.ScimagoUrl = `https://www.scimagojr.com/journalsearch.php?q=${metadata.issn.replace('-', '')}`;
                updateData.InternationalProofUrl = `https://mjl.clarivate.com/search-results?issn=${metadata.issn}&exact_match=true`;
            }

            if (metadata.volume) updateData.IssueNumber = metadata.volume;
            if (metadata.issue) updateData.IssueNumber = updateData.IssueNumber ? `${updateData.IssueNumber} (${metadata.issue})` : metadata.issue;

            if (metadata.first_page && metadata.last_page) updateData.PageRange = `${metadata.first_page} - ${metadata.last_page}`;
            else if (metadata.pages) updateData.PageRange = metadata.pages;

            setFormData(updateData);
            if (!metadata.internal_journal_id && !metadata.journal_name) toast?.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã trích xuất dữ liệu!', life: 3000 });

        } catch (err) {
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: err.message, life: 5000 });
        } finally {
            setIsExtracting(false);
        }
    };

    const handleCalculateBonus = () => {
        const currentUserStr = localStorage.getItem('user');
        const currentUserInfo = currentUserStr ? JSON.parse(currentUserStr) : null;
        const isCreator = !isEditing || (formData.UserId?.toString() === currentUserInfo?.Id?.toString());
        const isReadOnlyFormMode = isEditing && !(isCreator || isAdminMode);
        const finalDisabled = readOnly || isReadOnlyFormMode;

        if (finalDisabled) return;

        let a_coeff = 1.0;
        let message = "Loại công trình tính hệ số mặc định (1.0)";

        const contactAuthors = members.filter(m => m.IsContactAuthor || m.isContactAuthor);
        const journalScore = parseFloat(formData.JournalScore) || 0;

        if (journalScore >= 0.75) {
            if (contactAuthors.length > 0) {
                const hasUD = contactAuthors.some(m => m.IsUD || m.isUD);
                const hasNonUD = contactAuthors.some(m => !(m.IsUD || m.isUD));

                const hasUDWithMultiOrg = contactAuthors.some(m => (m.IsUD || m.isUD) && parseInt(m.OrganizationCount || m.organizationCount || 1) >= 2);

                if (hasUD && hasNonUD) {
                    a_coeff = 1.5;
                    message = "Tác giả liên hệ có người thuộc và không thuộc ĐHĐN (a = 1.5)";
                } else if (hasUD && !hasNonUD) {
                    if (hasUDWithMultiOrg) {
                        a_coeff = 1.5;
                        message = "Tác giả liên hệ thuộc ĐHĐN nhưng có từ 2 đơn vị công tác trở lên (a = 1.5)";
                    } else {
                        a_coeff = 2.0;
                        message = "Tất cả tác giả liên hệ đều thuộc ĐHĐN (a = 2.0)";
                    }
                } else if (!hasUD && hasNonUD) {
                    a_coeff = 1.0;
                    message = "Tất cả tác giả liên hệ không thuộc ĐHĐN (a = 1.0)";
                }
            } else {
                a_coeff = 1.0;
                message = "Công trình từ 0.75 điểm trở lên nhưng không có tác giả liên hệ (a = 1.0)";
            }
        }
        else {
            a_coeff = 1.0;
            message = "Công trình có điểm dưới 0.75, hệ số tác giả mặc định (a = 1.0)";
        }

        setFormData({ ...formData, BonusCoefficient: a_coeff.toFixed(1).toString() });
        toast?.current?.show({ severity: 'success', summary: 'Tính hệ số', detail: message, life: 4000 });
    };

    const handlePreviewScores = () => {
        const journalScore = parseFloat(formData.JournalScore) || 0;
        const a_coeff = parseFloat(formData.BonusCoefficient) || 1.0;

        const tickedPrimaryCount = members.filter(m => m.IsFirstAuthor || m.IsContactAuthor).length;
        const primaryAuthors = Math.max(parseInt(formData.PrimaryAuthors) || 1, tickedPrimaryCount);
        const totalAuthors = Math.max(parseInt(formData.TotalAuthors) || 1, members.length);

        const totalScore = journalScore * a_coeff;

        let primaryScore = totalScore;
        let coAuthorScore = 0;

        if (totalAuthors > 1) {
            const thirdPool = totalScore / 3;
            const twoThirdsPool = (totalScore * 2) / 3;

            primaryScore = (thirdPool / primaryAuthors) + (twoThirdsPool / totalAuthors);
            coAuthorScore = twoThirdsPool / totalAuthors;
        }

        let totalStudentPoints = 0;
        let instructorCount = 0;

        if (totalAuthors > 1) {
            members.forEach(m => {
                const isStudent = m.IsStudent || m.isStudent;
                const isInstructor = m.IsInstructor || m.isInstructor;
                const isPrimary = m.IsFirstAuthor || m.isFirstAuthor || m.IsContactAuthor || m.isContactAuthor;

                const mScore = isPrimary ? primaryScore : coAuthorScore;

                if (isStudent) totalStudentPoints += mScore;
                if (isInstructor) instructorCount++;
            });
        }

        const currentUser = JSON.parse(localStorage.getItem('user'));
        let yourScore = 0;

        if (currentUser && members.length > 0) {
            const myIndex = members.findIndex(m => m.UserId?.toString() === currentUser.Id?.toString());
            if (myIndex !== -1) {
                const me = members[myIndex];

                yourScore = (me.IsFirstAuthor || me.IsContactAuthor) ? primaryScore : coAuthorScore;

                const isMeInstructor = me.IsInstructor || me.isInstructor;
                if (isMeInstructor && instructorCount > 0 && journalScore >= 0.75) {
                    yourScore += (totalStudentPoints / instructorCount);
                }

                if ((parseInt(me.OrganizationCount) || 1) > 1) {
                    yourScore *= 0.5;
                }
            } else {
                yourScore = "Bạn chưa có tên trong danh sách tác giả!";
            }
        } else {
            yourScore = "Vui lòng chọn thành viên!";
        }

        setPreviewScores({
            primary: primaryScore.toFixed(4),
            coAuthor: totalAuthors > 1 ? coAuthorScore.toFixed(4) : "0.0000",
            yourScore: typeof yourScore === 'number' ? yourScore.toFixed(4) : yourScore
        });
    };

    const addMember = () => {
        if (readOnly) return;
        setMembers([...members, { UserId: '', FullName: '', Email: '', IsFirstAuthor: false, IsContactAuthor: false, IsUD: false, OrganizationCount: 1, IsStudent: false, IsInstructor: true }]);
    };

    const removeMember = (index) => {
        if (readOnly) return;
        setMembers(members.filter((_, i) => i !== index));
    };

    const handleMemberChange = (index, field, value) => {
        if (readOnly) return;
        const updated = [...members];
        updated[index][field] = value;

        if (field === 'UserId' && value) {
            const selectedUser = users.find(u => u.Id === value);
            if (selectedUser) {
                updated[index].FullName = `${selectedUser.LastName || ''} ${selectedUser.FirstName || ''}`.trim();
                updated[index].IsUD = false;
            }
        }

        if (field === 'IsStudent' && value) {
            updated[index].UserId = '';
            updated[index].IsInstructor = false;
            updated[index].IsUD = false;
        }

        if (field === 'IsInstructor' && value) {
            updated[index].IsStudent = false;
        }

        setMembers(updated);
    };

    const handleChange = (e) => {
        if (readOnly) return;
        const { name, value, type, checked } = e.target;

        let finalValue = type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? '' : Number(value)) : value);

        setFormData({
            ...formData,
            [name]: finalValue
        });
    };

    const handleDateChange = (name, dateObj) => {
        if (readOnly) return;
        let dateString = '';
        if (dateObj) {
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const dd = String(dateObj.getDate()).padStart(2, '0');
            dateString = `${yyyy}-${mm}-${dd}`;
        }
        handleChange({ target: { name, value: dateString, type: 'text' } });
    };

    const handleSelectJournal = (j) => {
        if (readOnly) return;
        setFormData({
            ...formData,
            JournalId: j.Id,
            JournalName: j.Name,
            ISSN_ISBN: j.ISSN || j.ISSN_ISBN,
            JournalType: j.JournalTypeName || j.JournalType || '',
            JournalScore: j.Score || 0,
            JournalStatus: j.Status,
            QRanking: '',
            BonusCoefficient: '1.0'
        });
        setIsJournalModalOpen(false);
    };

    const handleMultiFileChange = (e) => {
        if (readOnly) return;

        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
        const MAX_FILE_SIZE = 5 * 1024 * 1024;

        const allSelectedFiles = Array.from(e.target.files);
        if (allSelectedFiles.length === 0) return;

        const validFiles = allSelectedFiles.filter(file => {
            const isTypeValid = allowedTypes.includes(file.type);
            const fileName = file.name.toLowerCase();
            const isExtensionValid = allowedExtensions.some(ext => fileName.endsWith(ext));

            if (!isTypeValid && !isExtensionValid) {
                toast?.current?.show({ severity: 'warn', summary: 'Định dạng không hợp lệ', detail: `Tệp "${file.name}" không được hỗ trợ. Chỉ cho phép PDF, JPG/JPEG, PNG`, life: 5000 });
                return false;
            }

            if (file.size > MAX_FILE_SIZE) {
                toast?.current?.show({ severity: 'warn', summary: 'Dung lượng quá lớn', detail: `Tệp "${file.name}" vượt quá giới hạn 5MB. Vui lòng chọn tệp nhỏ hơn.`, life: 5000 });
                return false;
            }

            return true;
        });

        if (validFiles.length === 0) {
            e.target.value = null;
            return;
        }

        const newFilesArray = validFiles.map(file => ({
            fileObj: file,
            name: file.name,
            description: ''
        }));

        setPendingFiles(prev => [...prev, ...newFilesArray]);
        e.target.value = null;
    };

    const removeExistingFile = (identifier) => {
        if (readOnly) return;
        setDeletedFileIds(prev => [...prev, identifier]);
        const updated = existingFiles.filter(f => f.fileId !== identifier && f.url !== identifier && f.link !== identifier && f.data !== identifier);
        setFormData({ ...formData, EvidenceFileName: JSON.stringify(updated) });
    };

    const removePendingFile = (index) => {
        if (readOnly) return;
        setPendingFiles(pendingFiles.filter((_, i) => i !== index));
    };

    const downloadIndividualFile = (fileObj) => {
        let fileUrl = '';
        if (fileObj.fileId) {
            fileUrl = `${process.env.REACT_APP_API_BASE_URL}/downloadevidence?fileId=${fileObj.fileId}`;
        } else if (fileObj.url || fileObj.link || fileObj.URL || fileObj.data) {
            fileUrl = fileObj.url || fileObj.link || fileObj.URL || fileObj.data;
            if (fileUrl.startsWith('/due-bucket') || fileUrl.startsWith('/uploads')) {
                fileUrl = `https://data.tapchikhkt.vn${fileUrl}`;
            }
        }
        if (fileUrl) window.open(fileUrl, '_blank');
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        const currentUserStr = localStorage.getItem('user');
        const currentUserInfo = currentUserStr ? JSON.parse(currentUserStr) : null;
        const isCreator = !isEditing || (formData.UserId?.toString() === currentUserInfo?.Id?.toString());
        const isReadOnlyFormMode = isEditing && !(isCreator || isAdminMode);
        const finalDisabled = readOnly || isReadOnlyFormMode;

        if (finalDisabled) return;
        setIsSubmittingForm(true);

        const method = isEditing ? 'PUT' : 'POST';

        let cleanEvidence = [];
        try {
            const currentFiles = JSON.parse(formData.EvidenceFileName || '[]');
            cleanEvidence = currentFiles.filter(f => !f.fileId);
        } catch (e) { }

        const firstValidMember = members.find(m => m.UserId);
        const targetUserId = formData.UserId || (firstValidMember ? firstValidMember.UserId : currentUserInfo.Id);

        const payload = {
            ...formData,
            EvidenceFileName: JSON.stringify(cleanEvidence),
            UserId: targetUserId,
            MembersJSON: JSON.stringify(members),
            Id: isEditing ? formData.Id : 0
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticlemngt`, {
                method,
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json().catch(() => ({ status: 'error' }));

            if (response.ok && result.status !== 'error') {
                const finalArticleId = result.articleId || formData.SourceId || formData.Id;

                if (deletedFileIds.length > 0) {
                    await Promise.all(deletedFileIds.map(fId => {
                        if (typeof fId === 'number') {
                            return fetch(`${process.env.REACT_APP_API_BASE_URL}/deleteevidence?fileId=${fId}`, {
                                method: 'DELETE',
                                headers: getAuthHeaders()
                            });
                        }
                        return Promise.resolve();
                    }));
                }

                if (pendingFiles.length > 0) {
                    await Promise.all(pendingFiles.map(pf => {
                        const fd = new FormData();
                        fd.append('articleId', finalArticleId);
                        fd.append('description', pf.description);
                        fd.append('file', pf.fileObj);
                        return fetch(`${process.env.REACT_APP_API_BASE_URL}/uploadevidence`, {
                            method: 'POST',
                            headers: getAuthHeaders(),
                            body: fd
                        });
                    }));
                }

                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã lưu bài báo và tệp đính kèm!', life: 3000 });
                if (typeof fetchData === 'function') fetchData(false);
                onClose();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Không thể lưu', detail: result.message || 'Lưu bài báo thất bại!', life: 6000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsSubmittingForm(false);
        }
    };

    if (!isOpen) return null;

    const filteredJournals = journals.filter(j =>
        j.Name?.toLowerCase().includes(searchJournal.toLowerCase()) ||
        (j.ISSN || j.ISSN_ISBN)?.includes(searchJournal)
    );

    const commonInputStyle = {
        width: '100%', padding: '10px 12px', borderRadius: '6px',
        border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box'
    };

    const currentUserStr = localStorage.getItem('user');
    const currentUserInfo = currentUserStr ? JSON.parse(currentUserStr) : null;
    const isCreator = !isEditing || (formData.UserId?.toString() === currentUserInfo?.Id?.toString());
    const isReadOnlyFormMode = isEditing && !(isCreator || isAdminMode);
    const finalDisabled = readOnly || isReadOnlyFormMode;

    return (
        <>
            <div className="modal-overlay" style={{ zIndex: 1000 }}>
                <div className="modal-box" style={{ width: '90%', maxWidth: '1000px', maxHeight: '95vh', overflowY: 'auto' }}>
                    <div className="modal-header">
                        <h3>{isEditing ? (readOnly ? "Xem chi tiết Bài báo" : "Quản lý / Cập nhật Bài báo") : "Thêm mới Bài báo (Admin)"}</h3>
                        <button className="close-btn" onClick={onClose}>&times;</button>
                    </div>

                    <div className="modal-body" style={{ padding: '25px' }}>
                        {!readOnly && (
                            <div style={{
                                background: '#f8fafc', border: '1px solid #e2e8f0', padding: '20px',
                                borderRadius: '10px', marginBottom: '25px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                            }}>
                                <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: '#1e293b', fontSize: '15px' }}>
                                    Tự động điền dữ liệu từ DOI (Tùy chọn)
                                </label>
                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    <input
                                        type="text" className="form-input" value={doiInput}
                                        onChange={(e) => setDoiInput(e.target.value)}
                                        placeholder="Nhập đường link DOI" disabled={isExtracting}
                                        style={{ ...commonInputStyle, flex: '1 1 250px' }}
                                    />
                                    <button
                                        type="button" onClick={handleDoiExtraction} className="btn-submit"
                                        style={{
                                            backgroundColor: isExtracting ? '#94a3b8' : '#2196f3',
                                            padding: '0 20px', color: '#fff', border: 'none', borderRadius: '6px',
                                            cursor: isExtracting ? 'not-allowed' : 'pointer', fontWeight: '600',
                                            fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px',
                                            transition: 'background-color 0.2s', flexShrink: 0
                                        }} disabled={isExtracting}
                                    >
                                        {isExtracting ? (<><div className="spinner-mini"></div>Đang xử lý</>) : (<><i className="fa-solid fa-file-import"></i>Trích xuất</>)}
                                    </button>
                                </div>
                                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
                                    * Hệ thống sẽ tự động điền các mục: Tiêu đề, Tác giả, Năm, Ngày công bố, URL, Volume/Issue... nếu tìm thấy
                                </p>
                            </div>
                        )}

                        <form id="scientificArticlesMngtForm" onSubmit={handleFormSubmit}>

                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tiêu đề bài báo *</label>
                                <input
                                    type="text" name="Title" className="form-input" value={formData.Title || ''}
                                    onChange={handleChange} onBlur={handleTitleBlur} required
                                    disabled={finalDisabled}
                                    style={commonInputStyle}
                                />
                            </div>

                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tóm tắt</label>
                                <textarea name="Summary" className="form-input" rows="3" value={formData.Summary || ''}
                                    onChange={handleChange} disabled={finalDisabled}
                                    style={{ ...commonInputStyle, resize: 'vertical' }}></textarea>
                            </div>

                            <div className="form-group" style={{ marginBottom: '25px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Từ khóa</label>
                                <input type="text" name="Keywords" className="form-input" value={formData.Keywords || ''}
                                    onChange={handleChange} disabled={finalDisabled} style={commonInputStyle} />
                            </div>

                            <div style={{ borderBottom: '1px solid #eee', paddingBottom: '30px', marginBottom: '30px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                                    <label style={{ fontWeight: '600', color: '#1e293b', margin: 0 }}>Thông tin Tạp chí / Kỷ yếu hội thảo *</label>
                                    {!readOnly && (
                                        <button type="button" onClick={() => setIsJournalModalOpen(true)}
                                            style={{ backgroundColor: '#2196f3', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <i className="fa-solid fa-list-check"></i> Chọn tạp chí từ danh mục
                                        </button>
                                    )}
                                </div>
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="custom-table" style={{ width: '100%', fontSize: '13px', minWidth: '700px' }}>
                                        <thead style={{ backgroundColor: '#f9f9f9' }}>
                                            <tr>
                                                <th style={{ textAlign: 'left', padding: '10px' }}>Tạp chí</th>
                                                <th style={{ textAlign: 'left' }}>ISSN/ISBN</th>
                                                <th style={{ textAlign: 'left' }}>Loại tạp chí</th>
                                                <th style={{ textAlign: 'center' }}>Điểm công trình</th>
                                                <th style={{ textAlign: 'center' }}>Đã duyệt?</th>
                                                {!readOnly && <th width="80px">Thao tác</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.JournalName ? (
                                                <tr>
                                                    <td style={{ fontWeight: 'bold', padding: '10px' }}>
                                                        {formData.JournalName}
                                                        {!formData.JournalId && <span style={{ color: '#f59e0b', marginLeft: '5px', fontWeight: 'normal', fontStyle: 'italic' }}>(Chưa ánh xạ)</span>}
                                                    </td>
                                                    <td>{formData.ISSN_ISBN}</td>
                                                    <td>{formData.JournalType}</td>
                                                    <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{formData.JournalScore}</td>
                                                    <td style={{ textAlign: 'center' }}>{formData.JournalStatus === 'Đã duyệt' && <i className="fa-solid fa-circle-check" style={{ color: '#2ecc71' }}></i>}</td>
                                                    {!readOnly && (
                                                        <td style={{ textAlign: 'center' }}>
                                                            <i className="fa-solid fa-trash-can" style={{ color: '#e74c3c', cursor: 'pointer' }}
                                                                onClick={() => setFormData({ ...formData, JournalId: null, JournalName: '', ISSN_ISBN: '', JournalType: '', JournalScore: 0, JournalStatus: '' })}></i>
                                                        </td>
                                                    )}
                                                </tr>
                                            ) : (
                                                <tr>
                                                    <td colSpan={readOnly ? "5" : "6"} style={{ padding: '40px 0', textAlign: 'center' }}>
                                                        <i className="fa-solid fa-laptop-file" style={{ fontSize: '50px', color: '#3498db', opacity: 0.5, marginBottom: '10px' }}></i>
                                                        <div style={{ color: '#ff5252', fontSize: '15px', fontWeight: 'bold' }}>Chưa chọn tạp chí</div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="form-grid-2" style={{ marginBottom: '20px' }}>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Danh mục tạp chí *</label>
                                    <select name="JournalCategory" className="form-input" value={formData.JournalCategory || ''}
                                        onChange={handleChange} required disabled={finalDisabled} style={commonInputStyle}>
                                        <option value="">Chọn danh mục tạp chí</option>
                                        {journalCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ngôn ngữ *</label>
                                    <select name="LanguageName" className="form-input" value={formData.LanguageName || ''}
                                        onChange={handleChange} required disabled={finalDisabled} style={commonInputStyle}>
                                        <option value="">Chọn ngôn ngữ</option>
                                        {languageOptions.map(lang => (
                                            <option key={lang.code} value={lang.name}>{lang.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-grid-2" style={{ marginBottom: '20px' }}>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Lĩnh vực *</label>
                                    <select name="ResearchFieldId" className="form-input" value={formData.ResearchFieldId || ''}
                                        onChange={handleChange} required disabled={finalDisabled} style={commonInputStyle}>
                                        <option value="">Chọn lĩnh vực</option>
                                        {fields.map(f => <option key={f.Id} value={f.Id}>{f.Name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Loại bài viết *</label>
                                    <select name="ArticleTypeId" className="form-input" value={formData.ArticleTypeId || ''}
                                        onChange={handleChange} required disabled={finalDisabled} style={commonInputStyle}>
                                        <option value="">Chọn loại bài viết</option>
                                        {articleTypes.map(t => <option key={t.Id} value={t.Id}>{t.Name}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Số bài báo (Volume/Issue) *</label>
                                <input type="text" name="IssueNumber" className="form-input" value={formData.IssueNumber || ''}
                                    onChange={handleChange} required disabled={finalDisabled} style={commonInputStyle} />
                            </div>

                            <div className="form-grid-3" style={{ marginBottom: '20px' }}>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Từ trang - đến trang *</label>
                                    <input type="text" name="PageRange" className="form-input" value={formData.PageRange || ''}
                                        onChange={handleChange} required disabled={finalDisabled} style={commonInputStyle} />
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nhà xuất bản</label>
                                    <input type="text" name="Publisher" className="form-input" value={formData.Publisher || ''}
                                        onChange={handleChange} disabled={finalDisabled} style={commonInputStyle} />
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ngày công bố *</label>
                                    <Calendar
                                        id="PublicationDate"
                                        name="PublicationDate"
                                        value={(formData.PublicationDate && !isNaN(new Date(formData.PublicationDate).getTime())) ? new Date(formData.PublicationDate) : null}
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        onChange={(e) => handleDateChange('PublicationDate', e.value)}
                                        style={{ width: '100%' }}
                                        appendTo={document.body}
                                        panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                        inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                        disabled={finalDisabled}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group" style={{ marginBottom: '30px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Thuộc đề tài</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    <input type="checkbox" name="IsInsideProject" checked={formData.IsInsideProject}
                                        onChange={handleChange} disabled={finalDisabled} style={{ width: '18px', height: '18px' }} />
                                    <input type="text" name="ProjectCode" className="form-input" placeholder="Mã đề tài"
                                        value={formData.ProjectCode || ''} onChange={handleChange}
                                        disabled={!formData.IsInsideProject || finalDisabled}
                                        style={{ ...commonInputStyle, flex: '1 1 200px' }} />
                                </div>
                            </div>

                            <div style={{ borderTop: '1px solid #eee', paddingTop: '20px' }}>
                                <p style={{ fontWeight: '600', color: '#333', marginBottom: '15px' }}>Thông tin tác giả</p>

                                {isOverridden && (
                                    <div style={{ backgroundColor: '#fff3cd', color: '#856404', padding: '12px 15px', borderRadius: '6px', marginBottom: '15px', borderLeft: '4px solid #f39c12', fontSize: '13px', lineHeight: '1.5' }}>
                                        <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '8px', color: '#e67e22', fontSize: '16px' }}></i>
                                        <strong>Cảnh báo ghi đè dữ liệu:</strong> Bạn đang điều chỉnh số lượng tác giả khác với danh sách thực tế bên dưới (Thực tế: {expectedTotal} tác giả, {expectedPrimary} tác giả chính, {expectedContact} tác giả liên hệ). Hệ thống sẽ tính điểm theo con số bạn nhập. <strong>Ban quản lý và hệ thống sẽ không chịu trách nhiệm nếu kết quả tính điểm bị sai lệch!</strong>
                                    </div>
                                )}

                                <div className="form-grid-3" style={{ marginBottom: '15px' }}>
                                    <div className="form-group">
                                        <label style={{ fontSize: '13px' }}>Tổng số tác giả *</label>
                                        <input
                                            type="number"
                                            name="TotalAuthors"
                                            className="form-input"
                                            value={formData.TotalAuthors || 1}
                                            onChange={handleChange}
                                            min="1"
                                            required
                                            disabled={finalDisabled}
                                            style={commonInputStyle}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontSize: '13px' }}>Số tác giả chính *</label>
                                        <input
                                            type="number"
                                            name="PrimaryAuthors"
                                            className="form-input"
                                            value={formData.PrimaryAuthors || 1}
                                            onChange={handleChange}
                                            min="1"
                                            required
                                            disabled={finalDisabled}
                                            style={commonInputStyle}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontSize: '13px' }}>Số tác giả liên hệ *</label>
                                        <input
                                            type="number"
                                            name="ContactAuthors"
                                            className="form-input"
                                            value={formData.ContactAuthors || 1}
                                            onChange={handleChange}
                                            min="1"
                                            required
                                            disabled={finalDisabled}
                                            style={commonInputStyle}
                                        />
                                    </div>
                                </div>
                                <p style={{ fontSize: '12px', color: '#666', fontStyle: 'italic', marginBottom: '20px' }}>
                                    Lưu ý: Các trường số lượng tác giả được hệ thống tự động tính toán dựa trên danh sách thành viên thực hiện bên dưới. Bạn vẫn có thể tự điều chỉnh nếu cần thiết.
                                </p>

                                <label style={{ display: 'block', marginBottom: '15px', fontWeight: 'bold', color: '#d32f2f', fontSize: '13px' }}>
                                    DANH SÁCH THÀNH VIÊN THỰC HIỆN THUỘC TRƯỜNG *
                                </label>

                                {!readOnly && (
                                    <button
                                        type="button" onClick={addMember}
                                        disabled={finalDisabled}
                                        style={{
                                            padding: '6px 15px',
                                            backgroundColor: finalDisabled ? '#bdc3c7' : '#3498db',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: finalDisabled ? 'not-allowed' : 'pointer',
                                            marginBottom: '15px'
                                        }}
                                    >
                                        <i className="fa-solid fa-user-plus"></i> Chọn người dùng hoặc thêm Sinh viên
                                    </button>
                                )}

                                <div style={{ overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                    <table className="custom-table" style={{ width: '100%', fontSize: '13px', backgroundColor: '#fff', border: 'none', minWidth: '1200px', margin: 0 }}>
                                        <thead>
                                            <tr>
                                                <th width="5%" style={{ textAlign: 'center' }}>STT</th>
                                                <th width="25%" style={{ textAlign: 'left' }}>Họ tên</th>
                                                <th width="9%" style={{ textAlign: 'center' }}>Tác giả đầu</th>
                                                <th width="10%" style={{ textAlign: 'center' }}>Tác giả liên hệ</th>
                                                <th width="10%" style={{ textAlign: 'center' }}>Thuộc ĐHĐN</th>
                                                <th width="14%" style={{ textAlign: 'center' }}>Số lượng CQ công tác</th>
                                                <th width="9%" style={{ textAlign: 'center' }}>Là Sinh viên</th>
                                                <th width="13%" style={{ textAlign: 'center' }}>Là GV Hướng dẫn</th>
                                                {!readOnly && <th width="5%" style={{ textAlign: 'center' }}>Xóa</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {members.length === 0 ? (
                                                <tr><td colSpan={readOnly ? "8" : "9"} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>Chưa có thành viên nào</td></tr>
                                            ) : members.map((m, idx) => (
                                                <tr key={idx}>
                                                    <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                    <td>
                                                        {(m.IsStudent || m.isStudent) ? (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                <input
                                                                    type="text"
                                                                    className="form-input"
                                                                    placeholder="Nhập họ tên Sinh viên"
                                                                    value={m.FullName || m.fullName || ''}
                                                                    onChange={(e) => handleMemberChange(idx, 'FullName', e.target.value)}
                                                                    disabled={finalDisabled}
                                                                    style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                                                                    required
                                                                />
                                                                <input
                                                                    type="email"
                                                                    className="form-input"
                                                                    placeholder="<Mã sinh viên>@due.udn.vn"
                                                                    value={m.Email || m.email || ''}
                                                                    onChange={(e) => handleMemberChange(idx, 'Email', e.target.value)}
                                                                    disabled={finalDisabled}
                                                                    pattern="^[a-zA-Z0-9._%+-]+@due\.udn\.vn$"
                                                                    title="Vui lòng nhập đúng định dạng email đuôi @due.udn.vn"
                                                                    style={{ width: '100%', padding: '8px', boxSizing: 'border-box', border: '1px solid #3498db', backgroundColor: '#f0f8ff' }}
                                                                    required
                                                                />
                                                                <span style={{ fontSize: '11px', color: '#e74c3c', fontStyle: 'italic', lineHeight: '1.2' }}>
                                                                    * Nhập chính xác Email để hệ thống liên kết Mã sinh viên
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                <SearchableFocusDropdown
                                                                    value={m.UserId}
                                                                    options={userOptions}
                                                                    onChange={(e) => handleMemberChange(idx, 'UserId', e.value)}
                                                                    placeholder="Chọn GV nội bộ"
                                                                    panelUniqueClass={`custom-dropdown-panel-mngt-member-${idx}`}
                                                                    disabled={finalDisabled}
                                                                    itemTemplate={userItemTemplate}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    className="form-input"
                                                                    placeholder="Hoặc nhập tên GV ngoài trường"
                                                                    value={m.FullName || m.fullName || ''}
                                                                    onChange={(e) => handleMemberChange(idx, 'FullName', e.target.value)}
                                                                    disabled={finalDisabled}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '8px',
                                                                        boxSizing: 'border-box',
                                                                        backgroundColor: m.UserId ? '#f1f5f9' : '#fff'
                                                                    }}
                                                                    title="Nhập tên nếu là GV ngoài trường không có trong hệ thống"
                                                                />
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input type="checkbox" checked={m.IsFirstAuthor}
                                                            onChange={(e) => handleMemberChange(idx, 'IsFirstAuthor', e.target.checked)}
                                                            disabled={finalDisabled} />
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input type="checkbox" checked={m.IsContactAuthor}
                                                            onChange={(e) => handleMemberChange(idx, 'IsContactAuthor', e.target.checked)}
                                                            disabled={finalDisabled} />
                                                    </td>
                                                    <td className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={m.IsUD || m.isUD || false}
                                                            onChange={(e) => handleMemberChange(idx, 'IsUD', e.target.checked)}
                                                            disabled={finalDisabled}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input
                                                            type="number"
                                                            className="form-input"
                                                            style={{
                                                                width: '70px', padding: '5px', textAlign: 'center',
                                                                backgroundColor: finalDisabled ? '#f8fafc' : '#fff',
                                                                cursor: finalDisabled ? 'not-allowed' : 'text'
                                                            }}
                                                            value={m.OrganizationCount}
                                                            onChange={(e) => handleMemberChange(idx, 'OrganizationCount', e.target.value)}
                                                            min="1"
                                                            disabled={finalDisabled}
                                                        />
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input type="checkbox" checked={m.IsStudent || m.isStudent || false}
                                                            onChange={(e) => {
                                                                handleMemberChange(idx, 'IsStudent', e.target.checked);
                                                            }}
                                                            disabled={finalDisabled} />
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <input type="checkbox" checked={m.IsInstructor || m.isInstructor || false}
                                                            onChange={(e) => handleMemberChange(idx, 'IsInstructor', e.target.checked)}
                                                            disabled={finalDisabled} />
                                                    </td>
                                                    {!readOnly && (
                                                        <td style={{ textAlign: 'center' }}>
                                                            {!finalDisabled && (
                                                                <i className="fa-solid fa-trash-can" style={{ color: '#e74c3c', cursor: 'pointer' }}
                                                                    onClick={() => removeMember(idx)}></i>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="form-grid-3" style={{ marginTop: '30px', marginBottom: '25px' }}>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Danh sách tác giả nguyên bản *</label>
                                    <input type="text" name="OriginalAuthors" className="form-input" value={formData.OriginalAuthors || ''}
                                        onChange={handleChange} required disabled={finalDisabled} style={commonInputStyle} />
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Xếp hạng (Q-Ranking)</label>
                                    <select name="QRanking" className="form-input" value={formData.QRanking || ''}
                                        onChange={handleChange} disabled={finalDisabled} style={commonInputStyle}>
                                        <option value="">Không có / Chưa xét</option>
                                        <option value="Q1">Q1</option>
                                        <option value="Q2">Q2</option>
                                        <option value="Q3">Q3</option>
                                        <option value="Q4">Q4</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Hệ số tác giả *</label>
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                        <input type="text" name="BonusCoefficient" className="form-input"
                                            style={{ width: '80px', padding: '10px', textAlign: 'center', fontWeight: 'bold' }}
                                            value={formData.BonusCoefficient || '1.0'} onChange={handleChange} disabled={finalDisabled} />
                                        <button type="button" onClick={handleCalculateBonus} className="btn-submit"
                                            disabled={finalDisabled}
                                            style={{ padding: '0 15px', fontSize: '13px', backgroundColor: finalDisabled ? '#e2e8f0' : '#fff', color: finalDisabled ? '#94a3b8' : '#2196f3', border: `1px solid ${finalDisabled ? '#cbd5e1' : '#2196f3'}`, cursor: finalDisabled ? 'not-allowed' : 'pointer' }}>
                                            Tính hệ số
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: '25px' }}>
                                <button type="button" onClick={handlePreviewScores}
                                    style={{ padding: '8px 15px', fontSize: '13px', backgroundColor: '#fff', color: '#17a2b8', border: '1px solid #17a2b8', borderRadius: '4px', cursor: 'pointer', marginBottom: '10px' }}>
                                    Xem trước điểm
                                </button>
                                {previewScores && (
                                    <table className="custom-table" style={{ width: '100%', fontSize: '13px', backgroundColor: '#fff' }}>
                                        <tbody>
                                            <tr><td style={{ fontWeight: 'bold', width: '50%', textAlign: 'center' }}>Điểm của bạn</td><td style={{ textAlign: 'center' }}>{previewScores.yourScore}</td></tr>
                                            <tr><td style={{ fontWeight: 'bold', textAlign: 'center' }}>Điểm tác giả chính</td><td style={{ textAlign: 'center' }}>{previewScores.primary}</td></tr>
                                            <tr><td style={{ fontWeight: 'bold', textAlign: 'center' }}>Điểm đồng tác giả</td><td style={{ textAlign: 'center' }}>{previewScores.coAuthor}</td></tr>
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Liên kết đến bài báo (URL / DOI URL)</label>
                                <input type="text" name="ArticleUrl" className="form-input" value={formData.ArticleUrl || ''}
                                    onChange={handleChange} disabled={finalDisabled} style={commonInputStyle} />
                            </div>
                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Link xếp loại SCIMAGOJR (Nếu có)</label>
                                <input type="text" name="ScimagoUrl" className="form-input" value={formData.ScimagoUrl || ''}
                                    onChange={handleChange} disabled={finalDisabled} style={commonInputStyle} />
                            </div>
                            <div className="form-group" style={{ marginBottom: '25px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Minh chứng tạp chí uy tín (SCIE/Scopus/Web of Science...)</label>
                                <input type="text" name="InternationalProofUrl" className="form-input" value={formData.InternationalProofUrl || ''}
                                    onChange={handleChange} disabled={finalDisabled} style={commonInputStyle} />
                            </div>

                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tệp tin minh chứng (Toàn văn bài báo)</label>

                                <p style={{ color: '#e74c3c', fontSize: '12px', fontStyle: 'italic', marginBottom: '15px' }}>
                                    * Chú ý: Hệ thống chỉ nhận tệp minh chứng ở định dạng PDF, JPG/JPEG và PNG. Dung lượng tối đa 5MB / tệp
                                </p>

                                {(existingFiles.length > 0 || pendingFiles.length > 0) && (
                                    <div style={{ overflowX: 'auto' }}>
                                        <table className="custom-table" style={{ width: '100%', marginBottom: '15px', border: '1px solid #eee', minWidth: '600px' }}>
                                            <thead>
                                                <tr>
                                                    <th width="50px" style={{ textAlign: 'center' }}>TT</th>
                                                    <th>Mô tả</th>
                                                    <th width="35%">Minh chứng</th>
                                                    {!readOnly && !finalDisabled && <th width="80px" style={{ textAlign: 'center' }}>Chức năng</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {existingFiles.map((file, idx) => (
                                                    <tr key={`ext-${idx}`}>
                                                        <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                        <td>
                                                            <span style={{ fontSize: '13px', color: '#666' }}>{file.description || 'Không có mô tả'}</span>
                                                        </td>
                                                        <td>
                                                            <span onClick={() => downloadIndividualFile(file)}
                                                                style={{ color: '#3498db', cursor: 'pointer', wordBreak: 'break-all' }} title="Nhấn để xem/tải về">{file.name}</span>
                                                        </td>
                                                        {!readOnly && !finalDisabled && (
                                                            <td style={{ textAlign: 'center' }}>
                                                                <button type="button" onClick={() => removeExistingFile(file.fileId || file.url || file.link || file.data)}
                                                                    style={{ background: '#fff', border: '1px solid #e74c3c', color: '#e74c3c', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}>
                                                                    <i className="fa-solid fa-trash-can"></i>
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                                {pendingFiles.map((file, idx) => (
                                                    <tr key={`pend-${idx}`}>
                                                        <td style={{ textAlign: 'center' }}>{existingFiles.length + idx + 1}</td>
                                                        <td>
                                                            <input type="text" className="form-input" placeholder="Nhập mô tả"
                                                                value={file.description || ''} onChange={(e) => {
                                                                    const newFiles = [...pendingFiles];
                                                                    newFiles[idx].description = e.target.value;
                                                                    setPendingFiles(newFiles);
                                                                }}
                                                                style={{ width: '100%', padding: '8px', border: 'none', background: 'transparent', borderBottom: '1px solid #eee', borderRadius: 0 }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <span style={{ color: '#2ecc71', wordBreak: 'break-all' }}>{file.name} <i className="fa-solid fa-cloud-arrow-up"></i> (Mới)</span>
                                                        </td>
                                                        {!readOnly && !finalDisabled && (
                                                            <td style={{ textAlign: 'center' }}>
                                                                <button type="button" onClick={() => removePendingFile(idx)}
                                                                    style={{ background: '#fff', border: '1px solid #e74c3c', color: '#e74c3c', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}>
                                                                    <i className="fa-solid fa-trash-can"></i>
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {!readOnly && !finalDisabled && (
                                    <div className="sa-file-zone" style={{ border: '2px dashed #cbd5e1', padding: '20px', textAlign: 'center', borderRadius: '8px', position: 'relative', backgroundColor: '#f8fafc', cursor: 'pointer' }}>
                                        <input type="file" multiple accept=".pdf, .jpg, .jpeg, .png, application/pdf, image/jpeg, image/png" onChange={handleMultiFileChange} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} title="Nhấn để chọn tệp" />

                                        {(existingFiles.length > 0 || pendingFiles.length > 0) ? (
                                            <>
                                                <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '30px', color: '#94a3b8', marginBottom: '10px' }}></i>
                                                <p style={{ margin: 0, fontSize: '14px', color: '#475569', fontWeight: '500' }}>Đã chọn {existingFiles.length + pendingFiles.length} tệp</p>
                                                <span style={{ display: 'inline-block', marginTop: '10px', padding: '6px 15px', backgroundColor: '#e2e8f0', color: '#475569', borderRadius: '4px', fontSize: '13px', fontWeight: '600' }}>Thêm tệp khác</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '40px', color: '#3b82f6', marginBottom: '10px' }}></i>
                                                <p style={{ margin: 0, fontSize: '15px', color: '#1e293b', fontWeight: '500' }}>Click để tải lên minh chứng</p>
                                                <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: '#64748b' }}>(Kéo thả tệp vào đây)</p>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>

                    <div className="modal-footer" style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
                        {readOnly ? (
                            <button type="button" className="btn-submit" onClick={onClose}>Đóng</button>
                        ) : (
                            <>
                                <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmittingForm}>Hủy</button>
                                <button type="submit" form="scientificArticlesMngtForm" className="btn-submit" disabled={isSubmittingForm || finalDisabled}>
                                    {isSubmittingForm ? <><i className="fa-solid fa-spinner fa-spin"></i> Đang lưu...</> : "Hoàn tất"}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                .spinner-mini { width: 14px; height: 14px; border: 2px solid #fff; border-top: 2px solid transparent; border-radius: 50%; animation: spin-mini 0.8s linear infinite; }
                @keyframes spin-mini { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>

            {isJournalModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal-box" style={{ width: '90%', maxWidth: '1200px', maxHeight: '90vh' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div><h3>Danh sách tạp chí</h3></div>
                            <button className="close-btn" onClick={() => setIsJournalModalOpen(false)}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                                <input type="text" placeholder="Tìm tên tạp chí, ISSN" className="form-input"
                                    value={searchJournal} onChange={(e) => setSearchJournal(e.target.value)}
                                    style={{ ...commonInputStyle, padding: '12px', flex: 1 }} />
                                {!readOnly && (
                                    <button type="button" onClick={() => setIsCreateJournalModalOpen(true)}
                                        style={{ backgroundColor: '#1a9953', color: '#fff', border: 'none', padding: '0 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <i className="fa-solid fa-plus"></i> Thêm mới
                                    </button>
                                )}
                            </div>
                            <div style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'auto' }}>
                                <table className="custom-table" style={{ width: '100%', minWidth: '1000px' }}>
                                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                        <tr>
                                            <th width="50px">STT</th>
                                            <th>TẠP CHÍ</th>
                                            <th>ISSN/ISBN</th>
                                            <th>LOẠI TẠP CHÍ</th>
                                            <th style={{ textAlign: 'center' }}>ĐIỂM CÔNG TRÌNH</th>
                                            <th style={{ textAlign: 'center' }}>ĐÃ DUYỆT?</th>
                                            <th width="100px">CHỨC NĂNG</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredJournals.map((j, idx) => (
                                            <tr key={j.Id}>
                                                <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                <td style={{ fontWeight: 'bold' }}>{j.Name}</td>
                                                <td>{j.ISSN || j.ISSN_ISBN}</td>
                                                <td>{j.JournalTypeName || j.JournalType}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{j.Score}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    {j.Status === 'Đã duyệt' && <i className="fa-solid fa-circle-check" style={{ color: '#2ecc71' }}></i>}
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSelectJournal(j)}
                                                        style={{
                                                            padding: '5px 15px',
                                                            backgroundColor: '#fff',
                                                            border: '1px solid #2ecc71',
                                                            color: '#2ecc71',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <i className="fa-solid fa-check"></i> Chọn
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn-cancel" onClick={() => setIsJournalModalOpen(false)}>Quay về</button>
                        </div>
                    </div>
                </div>
            )}

            <JournalForm
                isOpen={isCreateJournalModalOpen}
                onClose={() => { setIsCreateJournalModalOpen(false); setJournalFormData(initialJournalForm); }}
                onSubmit={handleCreateJournalSubmit}
                formData={journalFormData}
                setFormData={setJournalFormData}
                isEditing={false}
                countries={countries}
                journalTypes={journalTypes || []}
                toast={toast}
                zIndex={3000}
            />
        </>
    );
};

export default ScientificArticlesMngtForm;